import { get, useFormContext, useWatch } from 'react-hook-form';
import CheckboxGroupInput from '@/components/form/CheckboxGroupInput.tsx';
import { Card, CardContent, InnerCardHeader } from '@/components/ui/Card.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import React, { useEffect, useMemo } from 'react';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import {
  getPaymentTimingOptions,
  PaymentTiming,
  TourInstallmentInputForm,
} from '@/features/dashboard/tourConfig/schemas/tourInstallmentsSchema.ts';
import { Trash } from 'lucide-react';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import { getPossiblePaymentsMethods } from '@/features/dashboard/tourConfig/types/PaymentMethod.utils.ts';
import { PriceRangeDto } from '@/features/shared/pricing/types/PriceRangeDto.ts';
import { getRangePricesRangeString } from '@/features/shared/pricing/util/PricingFormatter.ts';
import useBankAccountSync from '@/features/dashboard/tourConfig/hooks/useBankAccountSync.ts';
import useOnlinePaymentGuard from '@/features/dashboard/tourConfig/hooks/useOnlinePaymentGuard.ts';

const InstallmentPaymentCard = ({
  index,
  remove,
  totalInstallments,
  priceRanges,
}: {
  index: number;
  remove: (index: number) => void;
  totalInstallments: number;
  priceRanges: PriceRangeDto[];
}) => {
  const {
    control,
    formState: { errors },
    register,
    setValue,
    getValues,
  } = useFormContext<TourInstallmentInputForm>();

  const { onPaymentMethodsChange } = useBankAccountSync();
  const { guardOnlinePayment } = useOnlinePaymentGuard(
    `installments.${index}.paymentMethods`,
  );

  const isFirstInstallment = index === 0;
  const isLastInstallment = index === totalInstallments - 1;

  const firstInstallmentPaymentTiming = useWatch({
    control,
    name: 'installments.0.paymentTiming',
  });

  const priceNames = useMemo(
    () =>
      Array.from(
        { length: Math.max(totalInstallments - 1, 0) },
        (_, i) => `installments.${i}.basePricePercentage` as const,
      ),
    [totalInstallments],
  );

  const prices = useWatch({
    control,
    name: priceNames,
  });

  useEffect(() => {
    if (!isLastInstallment) return;
    const sum: number = prices.reduce(
      (acc: number, price) => acc + (Number(price) || 0),
      0,
    ) as number;
    const remainder = Math.max(100 - sum, 0);
    const current =
      Number(getValues(`installments.${index}.basePricePercentage`)) || 0;
    if (current !== remainder) {
      setValue(`installments.${index}.basePricePercentage`, remainder);
    }
  }, [isLastInstallment, prices, index, setValue, getValues]);

  return (
    <Card className="mb-0">
      <InnerCardHeader className="flex flex-row items-center justify-between py-1.5">
        <p className="text-sm font-semibold">
          Rata {index + 1}
          <span className="ml-2 text-[10px] font-light italic">
            {getRangePricesRangeString(priceRanges)}
          </span>
        </p>
        {totalInstallments > 2 && (
          <DangerIconButton onClick={() => remove(index)}>
            <Trash />
          </DangerIconButton>
        )}
      </InnerCardHeader>
      <CardContent className="space-y-4">
        {isFirstInstallment && (
          <ToggleInput
            control={control}
            fieldPath="installments.0.paymentTiming"
            label="Termin pierwszej raty"
            required
            options={getPaymentTimingOptions()}
            error={get(errors, 'installments.0.paymentTiming.message')}
          />
        )}

        <TwoInputsRow ratio="1/2">
          <ValueInput
            label="Procent kwoty podstawowej"
            type="number"
            register={register(`installments.${index}.basePricePercentage`)}
            error={get(
              errors,
              `installments.${index}.basePricePercentage.message`,
            )}
            disabled={isLastInstallment}
            required
            placeholder="0"
            sufix={'%'}
          />
          {isFirstInstallment ? (
            firstInstallmentPaymentTiming ===
              PaymentTiming.AFTER_RESERVATION && (
              <ValueInput
                label="Termin płatności pierwszej raty (po rezerwacji)"
                register={register('installments.0.dueDaysAfterReservation', {
                  valueAsNumber: true,
                })}
                error={get(
                  errors,
                  'installments.0.dueDaysAfterReservation.message',
                )}
                required
                placeholder="0"
                type="number"
                sufix="dni po rezerwacji"
                inputClassName="flex-1/3"
              />
            )
          ) : (
            <ValueInput
              label="Termin płatności raty (przed startem wyjazdu)"
              register={register(`installments.${index}.dueDaysBeforeStart`, {
                valueAsNumber: true,
              })}
              error={get(
                errors,
                `installments.${index}.dueDaysBeforeStart.message`,
              )}
              required
              placeholder="0"
              type="number"
              sufix="dni przed startem wyjazdu"
              inputClassName="flex-1/3"
            />
          )}
        </TwoInputsRow>
        <CheckboxGroupInput
          fieldPath={`installments.${index}.paymentMethods`}
          label="Sposób płatności"
          required
          control={control}
          options={getPossiblePaymentsMethods()}
          error={errors.installments?.[index]?.paymentMethods?.message}
          onChange={(values) => {
            guardOnlinePayment(values);
            onPaymentMethodsChange();
          }}
        />
      </CardContent>
    </Card>
  );
};

export default InstallmentPaymentCard;
